import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

/**
 * useCameraCapture — encapsulates getUserMedia / video stream / capture logic
 * so any page that lets the user photograph a document can reuse it.
 *
 * Returns:
 *   - videoRef:    bind to a <video> element
 *   - streaming:   boolean — true while the camera is active
 *   - startCamera: async () => start the back camera stream
 *   - stopCamera:  () => stop tracks and detach the stream
 *   - capture:     () => returns { dataUrl, base64 } from the current video frame
 *
 * Automatically stops the camera on unmount.
 */
export function useCameraCapture({ errorMessage = "Impossibile accedere alla fotocamera" } = {}) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [streaming, setStreaming] = useState(false);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((tk) => tk.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setStreaming(false);
  }, []);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } }, audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setStreaming(true);
    } catch {
      toast.error(errorMessage);
    }
  }, [errorMessage]);

  const capture = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return null;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.85);
    const base64 = dataUrl.split(",")[1] || "";
    stopCamera();
    return { dataUrl, base64 };
  }, [stopCamera]);

  // Auto-stop on unmount.
  useEffect(() => () => stopCamera(), [stopCamera]);

  return { videoRef, streaming, startCamera, stopCamera, capture };
}
